import { SignAsset, SignRenderer } from './sign-renderer.js';

export interface RotationItem {
  id: string;
  kind: 'image' | 'video';
  url: string;
  displayMs: number;
  transitionMs: number;
  enabled: boolean;
  source: 'default' | 'triggered' | 'news';
  caption?: string;
}

export class RotationEngine {
  private items: RotationItem[] = [];
  private idx = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private override: { item: RotationItem; clearAt?: number } | null = null;

  onItemChange: ((item: RotationItem | null) => void) | null = null;

  constructor(private renderer: SignRenderer) {}

  setItems(items: RotationItem[]): void {
    this.items = [...items];
    if (!this.override) this.kick();
  }

  enable(id: string): void { this.mutate(id, (i) => ({ ...i, enabled: true })); }
  disable(id: string): void { this.mutate(id, (i) => ({ ...i, enabled: false })); }
  setDisplayMs(id: string, ms: number): void { this.mutate(id, (i) => ({ ...i, displayMs: ms })); }
  setTransitionMs(id: string, ms: number): void { this.mutate(id, (i) => ({ ...i, transitionMs: ms })); }

  reorder(orderedIds: string[]): void {
    const map = new Map(this.items.map((i) => [i.id, i]));
    this.items = orderedIds.map((id) => map.get(id)).filter(Boolean) as RotationItem[];
    if (!this.override) this.kick();
  }

  appendOrReplace(item: RotationItem): void {
    const i = this.items.findIndex((x) => x.id === item.id);
    if (i >= 0) this.items[i] = item;
    else this.items.push(item);
    if (!this.override) this.kick();
  }

  swapDefaultsTo(replacements: RotationItem[]): void {
    const nonDefault = this.items.filter((i) => i.source !== 'default');
    this.items = [...replacements, ...nonDefault];
    if (!this.override) this.kick();
  }

  overrideWith(item: RotationItem, durationMs?: number): void {
    this.clear();  // ALWAYS clear pending tick before setting override
    this.override = durationMs !== undefined
      ? { item, clearAt: Date.now() + durationMs }
      : { item };
    this.show(item);
    if (durationMs) {
      this.timer = setTimeout(() => this.clearOverride(), durationMs);
    }
  }

  clearOverride(): void {
    this.override = null;
    this.onItemChange?.(null);
    this.kick();
  }

  visibleItems(): readonly RotationItem[] { return this.items.filter((i) => i.enabled); }

  /** True if the item with the given id is currently enabled in the rotation. */
  isEnabled(id: string): boolean {
    return this.items.find((i) => i.id === id)?.enabled ?? false;
  }

  /**
   * Snap the rotation to the item with the given id and start displaying it.
   * The item must already be enabled (use `enable(id)` first if not). If the
   * id isn't in the visible list, this is a no-op.
   */
  showNow(id: string): void {
    if (this.override) return;
    const list = this.visibleItems();
    const idx = list.findIndex((i) => i.id === id);
    if (idx < 0) return;
    this.clear();
    this.idx = idx;
    this.tick();
  }

  private mutate(id: string, fn: (i: RotationItem) => RotationItem): void {
    const i = this.items.findIndex((x) => x.id === id);
    if (i < 0) return;
    const next = fn(this.items[i]!);
    this.items[i] = next;
    if (!this.override) this.kick();
  }

  private kick(): void {
    this.clear();
    const list = this.visibleItems();
    if (list.length === 0) {
      this.renderer.show(null);
      return;
    }
    this.idx = this.idx % list.length;
    this.tick();
  }

  private tick(): void {
    if (this.override) return;  // defensive bail if override was set between schedule and fire
    const list = this.visibleItems();
    if (list.length === 0) return;
    const item = list[this.idx % list.length]!;
    this.show(item);
    this.timer = setTimeout(() => {
      if (this.override) return;
      this.idx = (this.idx + 1) % list.length;
      this.tick();
    }, item.displayMs);
  }

  private show(item: RotationItem): void {
    const asset: SignAsset = { kind: item.kind, url: item.url, displayMs: item.displayMs, transitionMs: item.transitionMs };
    this.renderer.show(asset);
    this.onItemChange?.(item);
  }

  private clear(): void {
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
  }
}
